import * as React from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';

import { analyserSeance, formatTemps, type LigneDetail, type Synthese } from './seanceEngine.ts';
import { useVma, saveLastVma } from '../Allures/useVma.ts';
import { useAllureTable } from '../Allures/useAllureTable.ts';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { nomDoublon, seancesIdentiques } from './doublons.ts';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Chip from '@mui/material/Chip';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ModelesIcon from '@mui/icons-material/AutoAwesomeOutlined';
import RetirerIcon from '@mui/icons-material/GroupRemoveOutlined';
import { ZoneChip } from '../Allures/couleursZones.tsx';
import { CHEMIN_GUIDE_NOTATION } from '../Guide/GuideNotation.tsx';
import {
  dupliquerSeance,
  duSocle,
  enLectureSeule,
  enregistrerSeance,
  listerSeances,
  retirerDuSocle,
  supprimerSeance,
  avertissementCalendriers,
  syntheseAEnregistrer,
  type Seance,
} from './seancesApi.ts';
import { classerSeance, TYPES_SEANCE, typeSeance, type TypeSeance } from './classification.ts';
import TypeSeanceChip from './TypeSeanceChip.tsx';
import { ajusterNotation, libelleAjustement, useAjustementRecup } from './ajustementRecup.ts';
import MenuItem from '@mui/material/MenuItem';
import ApercuIntervalsIcu from './ApercuIntervalsIcu.tsx';
import ProfilSeance from './ProfilSeance.tsx';
import { formaterDuree } from './formatDuree.ts';
import { DISTANCES } from './distanceCourse.ts';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import StarIcon from '@mui/icons-material/StarOutline';
import {
  CATEGORIES,
  categorie as infoCategorie,
  categorieDe,
  composerNotation,
  definirModeleParDefaut,
  libelleUsage,
  MODELE_PAR_DEFAUT_MODIFIE_EVENT,
  modelePour,
  partiesModele,
  aUnType,
  usageDe,
  usagesDuModele,
  USAGES_MODELE,
  USAGES_PAR_DEFAUT,
  type UsageParDefaut,
  type InfoUsageParDefaut,
  useModelesParDefaut,
  type Categorie,
  type CodeCategorie,
  type UsageModele,
  type InfoUsage,
} from './composition.ts';

const EXAMPLE = "6x(1000m@10K-R2') + 4x(8'@MP-R3')";

function formatDistanceKm(m: number): string {
  return `${(m / 1000).toFixed(2)} km`;
}

// Meme tableau triable que la page Allures (@mui/x-data-grid), plutot
// qu'un <Table> MUI classique.
const columns: GridColDef<LigneDetail & { id: number }>[] = [
  { field: 'nomZone', headerName: 'Zone', width: 170, renderCell: (params) => <ZoneChip nom={params.value} /> },
  { field: 'nbReps', headerName: 'Reps', type: 'number', width: 90 },
  {
    field: 'effortDistM',
    headerName: 'Distance effort',
    type: 'number',
    width: 150,
    valueFormatter: (value: number) => `${value} m`,
  },
  {
    field: 'effortTimeS',
    headerName: 'Temps effort',
    type: 'number',
    width: 130,
    valueFormatter: (value: number) => formatTemps(value),
  },
  {
    field: 'recuperationS',
    headerName: 'Récupération',
    type: 'number',
    width: 140,
    // Le tri se fait sur la valeur en secondes, mais l'affichage reprend
    // la meme notation que la saisie (ex: "1'30''"), pas un "MM:SS".
    renderCell: (params) => (params.row.recuperationS ? params.row.recuperationAffichage : '-'),
  },
  {
    field: 'dureeTotaleS',
    headerName: 'Durée totale',
    type: 'number',
    width: 140,
    valueFormatter: (value: number) => formatTemps(value),
  },
  {
    field: 'distanceTotaleM',
    headerName: 'Distance totale',
    type: 'number',
    width: 160,
    valueFormatter: (value: number) => `${value} m`,
  },
  {
    field: 'ua',
    headerName: 'UA',
    type: 'number',
    width: 90,
    valueGetter: (value: number | null) => (value === null || value === undefined ? null : Math.round(value)),
  },
  { field: 'rpe', headerName: 'RPE', type: 'number', width: 90 },
  {
    field: 'checks',
    headerName: 'Vérifications',
    width: 360,
    sortable: false,
    valueGetter: (_value, row) => [row.checkDistance, row.checkReps, row.checkVolume, row.checkRecup].join('\n'),
    renderCell: (params) => (
      <div style={{ whiteSpace: 'pre-line', fontSize: '0.8rem', lineHeight: 1.4, padding: '8px 0' }}>
        {params.value}
      </div>
    ),
  },
];

/**
 * Editeur d'une seance (page /construction/nouvelle ou /construction/:id),
 * ouvert depuis la liste "Mes seances" (MesSeances.tsx, page /construction).
 */
export default function Construction() {
  const navigate = useNavigate();
  // Id de la seance a ouvrir (absent pour une nouvelle seance).
  const { id: idUrl } = useParams();
  // Seance a pre-remplir (ex : envoyee depuis le generateur de seances).
  const location = useLocation();
  // La VMA de reference vient de la préférence enregistrée pour
  // l'utilisateur (calculée à partir de son dernier record saisi sur
  // l'écran Allures, cf AllureController/UserPreferenceService côté
  // serveur). Si elle n'existe pas encore, useVma renvoie l'utilisateur
  // saisir un record sur cet écran.
  const [vma, setVma] = useVma();
  // Parametres d'allures de l'utilisateur (ou valeurs par defaut), cf
  // la page Mes préférences.
  const { table } = useAllureTable();
  // Ajustement des recuperations (preference) applique aux calculs.
  const ajustementRecup = useAjustementRecup();
  const [notation, setNotation] = React.useState(EXAMPLE);
  const notationAjustee = React.useMemo(() => ajusterNotation(notation, ajustementRecup), [notation, ajustementRecup]);
  const [synthese, setSynthese] = React.useState<Synthese | undefined>();
  const [detail, setDetail] = React.useState<LigneDetail[]>([]);

  const handleGenerate = (e: React.FormEvent) => {
    e.preventDefault();
    saveLastVma(vma);
    const resultat = analyserSeance(ajusterNotation(notation, ajustementRecup), vma, table);
    setSynthese(resultat.synthese);
    setDetail(resultat.detail);
  };

  // Seance en cours d'edition : nouvelle (sans id) ou ouverte depuis la
  // liste "Mes seances" (avec id).
  const [seanceId, setSeanceId] = React.useState<string | undefined>();
  // Seance du socle commun que l'utilisateur ne peut pas modifier (il la duplique pour l'adapter).
  const [lectureSeule, setLectureSeule] = React.useState(false);
  const [socle, setSocle] = React.useState(false);
  // Id de la seance deja chargee dans l'editeur depuis l'adresse.
  const ouverte = React.useRef<string | undefined>(undefined);
  const [nom, setNom] = React.useState('');
  const [notes, setNotes] = React.useState('');
  // Type de seance corrige a la main ('' = automatique, deduit du detail).
  const [typeManuel, setTypeManuel] = React.useState<string>('');
  const typeAuto = React.useMemo(
    () => (synthese && !synthese.erreur ? classerSeance(detail, table) : null),
    [synthese, detail, table],
  );
  const [erreurNom, setErreurNom] = React.useState<string | null>(null);
  const [enregistrement, setEnregistrement] = React.useState(false);
  const [rafraichissement, setRafraichissement] = React.useState(0);
  // Composition : categorie de la seance editee et, pour un modele, ses
  // parties (ids de seances d'echauffement / de recuperation).
  const [categorie, setCategorie] = React.useState<CodeCategorie>('CORPS');
  const [idEchauffement, setIdEchauffement] = React.useState('');
  const [idRecuperation, setIdRecuperation] = React.useState('');
  // Modele : distance de course a laquelle il est destine ('' = toutes distances).
  const [distanceCourse, setDistanceCourse] = React.useState('');
  const [seances, setSeances] = React.useState<Seance[]>([]);
  // Modeles par defaut (repetitions / sortie continue) et, pour un corps de
  // seance, modele force a la main ('' = automatique selon le type).
  const modeles = useModelesParDefaut(rafraichissement);
  const [modeleManuel, setModeleManuel] = React.useState<string>('');
  const usageAuto = usageDe(null, typeManuel || typeAuto);
  const usage: UsageModele = usageDe(modeleManuel || null, typeManuel || typeAuto);
  // Modele applique au corps de seance edite (pour l'apercu).
  const modeleApplique = categorie === 'CORPS' ? modelePour(modeles, usage) : null;
  const estModele = categorie === 'MODELE';
  const echauffements = seances.filter((s) => categorieDe(s) === 'ECHAUFFEMENT');
  const recuperations = seances.filter((s) => categorieDe(s) === 'RECUPERATION');
  const partiesDefaut = partiesModele(modeleApplique, seances);
  // Parties du modele en cours d'edition.
  const echauffementChoisi = seances.find((s) => s.id === idEchauffement);
  const recuperationChoisie = seances.find((s) => s.id === idRecuperation);
  const [message, setMessage] = React.useState<{ severity: 'success' | 'error'; texte: string } | null>(null);

  // Enregistre la seance (creation, ou mise a jour si elle a ete ouverte
  // depuis la liste ; `commeNouvelle` force une copie). La synthese est
  // recalculee au moment de l'enregistrement avec la VMA et les allures
  // courantes, et memorisee pour le tri de la liste.
  const enregistrer = async (commeNouvelle: boolean) => {
    if (!nom.trim()) {
      setErreurNom('Donne un nom à la séance pour l\'enregistrer');
      return;
    }
    if (estModele) {
      if (!idEchauffement && !idRecuperation) {
        setMessage({ severity: 'error', texte: 'Choisis un échauffement, une récupération, ou les deux.' });
        return;
      }
      await envoyer({
        id: commeNouvelle ? undefined : seanceId,
        nom: nom.trim(),
        notation: '',
        notes: notes.trim() || null,
        categorie,
        idEchauffement: idEchauffement || null,
        idRecuperation: idRecuperation || null,
        distanceCourse: distanceCourse || null,
      });
      return;
    }
    // Calculs sur la notation ajustee (preference de recuperation) ; la
    // notation modele, elle, est enregistree telle quelle.
    const resultat = analyserSeance(ajusterNotation(notation, ajustementRecup), vma, table);
    setSynthese(resultat.synthese);
    setDetail(resultat.detail);
    // Nouvelle seance (ou copie) identique a une seance deja enregistree.
    const doublons = seancesIdentiques(notation, seances, vma, table, commeNouvelle ? undefined : seanceId);
    if (doublons.length > 0 && (commeNouvelle || !seanceId)
      && !window.confirm(`Une séance identique existe déjà : ${nomDoublon(doublons[0])}. Enregistrer quand même ?`)) {
      return;
    }
    if (resultat.synthese.erreur) {
      setMessage({ severity: 'error', texte: 'Séance non enregistrée : ' + resultat.synthese.erreur });
      return;
    }
    const seance: Seance = {
      id: commeNouvelle ? undefined : seanceId,
      nom: nom.trim(),
      notation: notation.trim(),
      notes: notes.trim() || null,
      ...syntheseAEnregistrer(resultat.synthese, resultat.detail, vma),
      // Le type (court, seuil...) ne concerne que les corps de seance.
      typeSeance: aUnType(categorie) ? typeManuel || classerSeance(resultat.detail, table) : null,
      typeForce: aUnType(categorie) && !!typeManuel,
      categorie,
      modeleSeance: categorie === 'CORPS' ? modeleManuel || null : null,
    };
    await envoyer(seance);
  };

  const envoyer = async (seance: Seance) => {
    setEnregistrement(true);
    try {
      const enregistree = await enregistrerSeance(seance);
      setSeanceId(enregistree.id);
      setRafraichissement((n) => n + 1);
      // Nouvelle seance (ou copie) : l'adresse suit la seance enregistree.
      if (!seance.id && enregistree.id) {
        ouverte.current = enregistree.id;
        navigate(`/seances/${enregistree.id}`, { replace: true });
      }
      setMessage({
        severity: 'success',
        texte: seance.id ? `Séance « ${enregistree.nom} » mise à jour.` : `Séance « ${enregistree.nom} » enregistrée.`,
      });
    } catch (e) {
      setMessage({ severity: 'error', texte: 'Séance non enregistrée : ' + (e as Error).message });
    } finally {
      setEnregistrement(false);
    }
  };

  // Ouvre une seance de la liste dans l'editeur et recalcule son apercu.
  const ouvrir = (seance: Seance) => {
    setSeanceId(seance.id);
    setLectureSeule(enLectureSeule(seance));
    setSocle(duSocle(seance));
    setNom(seance.nom);
    setNotes(seance.notes ?? '');
    setTypeManuel(seance.typeForce && seance.typeSeance ? seance.typeSeance : '');
    setNotation(seance.notation);
    setErreurNom(null);
    const cat = categorieDe(seance);
    setCategorie(cat);
    setModeleManuel(seance.modeleSeance ?? '');
    setIdEchauffement(seance.idEchauffement ?? '');
    setDistanceCourse(seance.distanceCourse ?? '');
    setIdRecuperation(seance.idRecuperation ?? '');
    if (cat === 'MODELE') {
      setSynthese(undefined);
      setDetail([]);
      return;
    }
    const resultat = analyserSeance(ajusterNotation(seance.notation, ajustementRecup), vma, table);
    setSynthese(resultat.synthese);
    setDetail(resultat.detail);
  };

  const changerCategorie = (nouvelle: CodeCategorie) => {
    setCategorie(nouvelle);
    if (nouvelle === 'MODELE') {
      setSynthese(undefined);
      setDetail([]);
    }
  };

  const choisirCommeModeleParDefaut = (u: UsageParDefaut) => {
    if (!seanceId) return;
    definirModeleParDefaut(u, seanceId)
      .then(() => setMessage({ severity: 'success', texte: `« ${nom.trim()} » est ton modèle « ${libelleUsage(u)} ».` }))
      .catch((e: Error) => setMessage({ severity: 'error', texte: 'Modèle non défini : ' + e.message }));
  };
  const usagesDefaut = usagesDuModele(seanceId, modeles);

  const dureeParties = [echauffementChoisi, recuperationChoisie]
    .filter((x): x is Seance => !!x)
    .reduce((t, x) => t + (x.dureeTotaleS ?? 0), 0);

  // Supprime la seance ouverte dans l'editeur (refus du serveur si c'est un
  // echauffement / une recuperation utilise par un modele).
  const supprimer = async () => {
    if (!seanceId) return;
    const avertissement = await avertissementCalendriers(seanceId);
    if (!window.confirm(`Supprimer « ${nom.trim() || 'cette séance'} » ? Les séances déjà posées dans le calendrier restent en place.` + avertissement)) return;
    setEnregistrement(true);
    try {
      await supprimerSeance(seanceId);
      if (usagesDuModele(seanceId, modeles).length > 0) window.dispatchEvent(new Event(MODELE_PAR_DEFAUT_MODIFIE_EVENT));
      // Retour a la liste, qui affiche la confirmation.
      navigate('/seances', { state: { message: `« ${nom.trim()} » supprimée.` } });
    } catch (e) {
      setMessage({ severity: 'error', texte: 'Suppression impossible : ' + (e as Error).message });
    } finally {
      setEnregistrement(false);
    }
  };

  // Copie personnelle d'une seance du socle commun, ouverte dans l'editeur.
  const dupliquer = async () => {
    if (!seanceId) return;
    setEnregistrement(true);
    try {
      const copie = await dupliquerSeance(seanceId);
      setRafraichissement((n) => n + 1);
      if (copie.id) {
        ouverte.current = copie.id;
        ouvrir(copie);
        navigate(`/seances/${copie.id}`, { replace: true });
      }
      setMessage({ severity: 'success', texte: `« ${copie.nom} » est maintenant dans tes séances : adapte-la à ta façon.` });
    } catch (e) {
      setMessage({ severity: 'error', texte: 'Copie impossible : ' + (e as Error).message });
    } finally {
      setEnregistrement(false);
    }
  };

  // Ne plus partager la seance du socle (responsable) : elle redevient personnelle.
  const retirer = async () => {
    if (!seanceId) return;
    const avertissement = await avertissementCalendriers(seanceId);
    if (!window.confirm(`Retirer « ${nom} » du socle commun ? Elle ne sera plus partagée : elle redevient une de tes séances personnelles. Les séances déjà posées dans les calendriers sont conservées.` + avertissement)) return;
    setEnregistrement(true);
    try {
      const retiree = await retirerDuSocle(seanceId);
      setRafraichissement((n) => n + 1);
      ouvrir(retiree);
      setMessage({ severity: 'success', texte: `« ${retiree.nom} » n'est plus partagée : c'est maintenant une de tes séances.` });
    } catch (e) {
      setMessage({ severity: 'error', texte: 'Retrait du socle impossible : ' + (e as Error).message });
    } finally {
      setEnregistrement(false);
    }
  };

  const nouvelleSeance = () => {
    setSeanceId(undefined);
    setLectureSeule(false);
    setSocle(false);
    setNom('');
    setNotes('');
    setTypeManuel('');
    setNotation('');
    setIdEchauffement('');
    setDistanceCourse('');
    setIdRecuperation('');
    setModeleManuel('');
    setSynthese(undefined);
    setDetail([]);
    setErreurNom(null);
  };

  // Seances de l'utilisateur : echauffements / recuperations / modeles
  // proposes dans l'editeur, et seance a ouvrir d'apres l'adresse.
  React.useEffect(() => {
    listerSeances()
      .then(setSeances)
      .catch((e: Error) => setMessage({ severity: 'error', texte: 'Séances indisponibles : ' + e.message }));
  }, [rafraichissement]);

  // Seances deja enregistrees identiques a la notation saisie (doublons).
  const identiques = React.useMemo(
    () => (estModele ? [] : seancesIdentiques(notation, seances, vma, table, seanceId)),
    [estModele, notation, seances, vma, table, seanceId],
  );

  // Seance envoyee depuis une autre page (generateur) : pre-remplissage d'une
  // nouvelle seance, une seule fois par navigation.
  const preRemplie = React.useRef<string | undefined>(undefined);
  React.useEffect(() => {
    const etat = location.state as { notation?: string; nom?: string; categorie?: CodeCategorie } | null;
    if (idUrl || !etat?.notation || preRemplie.current === location.key) return;
    preRemplie.current = location.key;
    nouvelleSeance();
    setNotation(etat.notation);
    setNom(etat.nom ?? '');
    setCategorie(etat.categorie ?? 'CORPS');
    const resultat = analyserSeance(ajusterNotation(etat.notation, ajustementRecup), vma, table);
    setSynthese(resultat.synthese);
    setDetail(resultat.detail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idUrl, location.key]);

  // Ouverture de la seance designee par l'adresse (une seule fois par id).
  const [introuvable, setIntrouvable] = React.useState(false);
  React.useEffect(() => {
    if (!idUrl) {
      if (ouverte.current !== undefined) {
        ouverte.current = undefined;
        // Pas de remise a zero si la page est pre-remplie (cf ci-dessus).
        if (!(location.state as { notation?: string } | null)?.notation) nouvelleSeance();
      }
      return;
    }
    if (ouverte.current === idUrl || seances.length === 0) return;
    const seance = seances.find((s) => s.id === idUrl);
    if (seance) {
      ouverte.current = idUrl;
      setIntrouvable(false);
      ouvrir(seance);
    } else {
      setIntrouvable(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idUrl, seances]);

  return (
    <Box component="main" sx={{ p: 12, pt: 10 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Button component={Link} to="/seances" startIcon={<ArrowBackIcon />}>
          Mes séances
        </Button>
        {!idUrl && (
          <Button component={Link} to="/modeles" variant="outlined" startIcon={<ModelesIcon />} sx={{ ml: 'auto' }}>
            Partir d'une séance type
          </Button>
        )}
      </Box>
      {introuvable && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Séance introuvable : elle a peut-être été supprimée. <Link to="/seances">Retour à la liste</Link>
        </Alert>
      )}

      <Typography sx={{ mb: 2 }}>
        Saisis une séance avec la notation habituelle, par exemple : <code>{EXAMPLE}</code>
        <br />
        Toutes les règles (allures, récupérations, séries, côtes...) et des exemples sont dans le{' '}
        <Link to={CHEMIN_GUIDE_NOTATION}>guide de la notation</Link>. Les % VMA et bornes de chaque allure se règlent
        dans les <Link to="/preferences?onglet=allures">préférences</Link>.
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          exclusive
          color="primary"
          size="small"
          value={categorie}
          onChange={(_e, valeur: CodeCategorie | null) => valeur && changerCategorie(valeur)}
        >
          {CATEGORIES.map((c: Categorie) => (
            <ToggleButton key={c.code} value={c.code}>{c.libelle}</ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Typography variant="body2" color="text.secondary">{infoCategorie(categorie).description}</Typography>
      </Box>

      {estModele ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <TextField
              select
              label="Échauffement"
              value={idEchauffement}
              onChange={(e) => setIdEchauffement(e.target.value)}
              helperText={echauffements.length === 0 ? 'Aucun échauffement enregistré pour l\'instant' : ' '}
              sx={{ minWidth: 280 }}
            >
              <MenuItem value="">Aucun</MenuItem>
              {echauffements.map((x: Seance) => <MenuItem key={x.id} value={x.id}>{x.nom}</MenuItem>)}
            </TextField>
            <Typography sx={{ mt: 2 }} color="text.secondary">+ corps de séance +</Typography>
            <TextField
              select
              label="Récupération"
              value={idRecuperation}
              onChange={(e) => setIdRecuperation(e.target.value)}
              helperText={recuperations.length === 0 ? 'Aucune récupération enregistrée pour l\'instant' : ' '}
              sx={{ minWidth: 280 }}
            >
              <MenuItem value="">Aucune</MenuItem>
              {recuperations.map((x: Seance) => <MenuItem key={x.id} value={x.id}>{x.nom}</MenuItem>)}
            </TextField>
          </Box>
          <TextField
            select
            label="Course"
            value={distanceCourse}
            onChange={(e) => setDistanceCourse(e.target.value)}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            helperText="Distance préparée par les athlètes à qui ce modèle est destiné"
            sx={{ minWidth: 280, mt: 1 }}
          >
            <MenuItem value="">Toutes distances</MenuItem>
            {DISTANCES.map((d) => <MenuItem key={d.code} value={d.code}>{d.libelle}</MenuItem>)}
          </TextField>
          {(echauffementChoisi || recuperationChoisie) && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Composition : <code>{composerNotation(echauffementChoisi?.notation, '[corps de séance]', recuperationChoisie?.notation)}</code>
              {dureeParties > 0 && <> — {formaterDuree(dureeParties)} en plus du corps de séance</>}
            </Typography>
          )}
        </Paper>
      ) : (
      <form onSubmit={handleGenerate}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <TextField
            id="vma"
            label="VMA (km/h)"
            type="number"
            value={vma}
            onChange={(e) => setVma(parseFloat(e.target.value))}
            inputProps={{ step: 0.1, min: 8, max: 30 }}
            sx={{ minWidth: 140 }}
          />
          <TextField
            id="notation"
            label="Notation de la séance"
            value={notation}
            onChange={(e) => setNotation(e.target.value)}
            sx={{ minWidth: 480 }}
          />
          <Button type="submit" variant="contained" sx={{ mt: 1 }}>
            Générer l'aperçu
          </Button>
        </Box>
        {ajustementRecup !== 0 && notation.trim() && notationAjustee !== notation && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Avec ton ajustement des récupérations ({libelleAjustement(ajustementRecup)}, cf{' '}
            <Link to="/preferences?onglet=recuperations">préférences</Link>) : <code>{notationAjustee}</code>
          </Typography>
        )}
        {categorie === 'CORPS' && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {usage === 'AUCUN' ? (
              <>Dans le calendrier, cette séance est posée seule (aucun modèle choisi pour elle).</>
            ) : modeleApplique ? (
              <>
                Dans le calendrier, cette séance est composée avec ton modèle « {modeleApplique.nom} » ({libelleUsage(usage)}) :{' '}
                <code>{composerNotation(partiesDefaut.echauffement?.notation, notation.trim() || '…', partiesDefaut.recuperation?.notation)}</code>
              </>
            ) : (
              <>
                Aucun modèle de séance défini : la séance sera posée seule dans le calendrier (crée un modèle puis
                choisis-le dans les <Link to="/preferences?onglet=modeles">préférences</Link>).
              </>
            )}
          </Typography>
        )}
        {categorie === 'COMPETITION' && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Compétition : décris la course avec la notation habituelle (ex : <code>10km@10K</code>,{' '}
            <code>21.1km@HP</code>) pour en estimer la durée et la charge ; elle se pose telle quelle dans le calendrier.
          </Typography>
        )}
        {categorie === 'SEANCE' && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Séance complète : elle se pose telle quelle dans le calendrier, sans modèle de séance.
          </Typography>
        )}
        {(categorie === 'ECHAUFFEMENT' || categorie === 'RECUPERATION') && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Cette séance ne se pose pas seule dans le calendrier : utilise-la dans un modèle de séance.
          </Typography>
        )}
      </form>
      )}

      {identiques.length > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {identiques.length === 1 ? 'Une séance identique est déjà enregistrée : ' : 'Des séances identiques sont déjà enregistrées : '}
          {identiques.map((x: Seance, i: number) => (
            <React.Fragment key={x.id}>
              {i > 0 && ', '}
              <Link to={`/seances/${x.id}`}>{nomDoublon(x)}</Link>
            </React.Fragment>
          ))}
          .
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {(seanceId ? '' : 'Nouveau : ') + infoCategorie(categorie).libelle}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <TextField
            label="Nom de la séance"
            placeholder="ex : Seuil long — 4x2000"
            value={nom}
            onChange={(e) => {
              setNom(e.target.value);
              setErreurNom(null);
            }}
            error={!!erreurNom}
            helperText={erreurNom ?? ' '}
            inputProps={{ maxLength: 100 }}
            sx={{ minWidth: 280 }}
          />
          <TextField
            label="Notes"
            placeholder="Facultatif : objectif, sensations, terrain…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            helperText=" "
            inputProps={{ maxLength: 1000 }}
            sx={{ minWidth: 340, flex: 1 }}
          />
          {aUnType(categorie) && (
          <TextField
            select
            label="Type de séance"
            value={typeManuel}
            onChange={(e) => setTypeManuel(e.target.value)}
            helperText={typeManuel ? 'Corrigé à la main' : 'Déduit de la notation'}
            sx={{ minWidth: 240 }}
          >
            <MenuItem value="">
              Automatique{typeAuto ? ` (${typeSeance(typeAuto)?.libelle})` : ''}
            </MenuItem>
            {TYPES_SEANCE.map((t: TypeSeance) => (
              <MenuItem key={t.code} value={t.code}>{t.libelle}</MenuItem>
            ))}
          </TextField>
          )}
          {categorie === 'CORPS' && (
          <TextField
            select
            label="Modèle de séance"
            value={modeleManuel}
            onChange={(e) => setModeleManuel(e.target.value)}
            helperText={modeleManuel ? 'Choisi à la main' : 'Selon le type de séance'}
            sx={{ minWidth: 240 }}
          >
            <MenuItem value="">Automatique ({libelleUsage(usageAuto)})</MenuItem>
            {USAGES_MODELE.map((u: InfoUsage) => <MenuItem key={u.code} value={u.code}>{u.libelle}</MenuItem>)}
            <MenuItem value="AUCUN">Aucun</MenuItem>
          </TextField>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {lectureSeule && (
            <Alert severity="info" sx={{ width: '100%' }}>
              Séance de la bibliothèque de ton club : seuls ses bibliothécaires la modifient. Duplique-la dans tes séances
              pour l'adapter à ta façon.
            </Alert>
          )}
          {lectureSeule && seanceId && (
            <Button variant="contained" startIcon={<ContentCopyIcon />} onClick={dupliquer} disabled={enregistrement}>
              Dupliquer dans mes séances
            </Button>
          )}
          {!lectureSeule && (
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={() => enregistrer(false)}
            disabled={enregistrement || (!estModele && !notation.trim())}
          >
            {seanceId ? (socle ? 'Enregistrer les modifications du socle' : 'Enregistrer les modifications') : 'Enregistrer la séance'}
          </Button>
          )}
          {seanceId && !lectureSeule && (
            <Button startIcon={<ContentCopyIcon />} onClick={() => enregistrer(true)} disabled={enregistrement || (!estModele && !notation.trim())}>
              Enregistrer comme nouvelle séance
            </Button>
          )}
          <Button
            startIcon={<AddIcon />}
            onClick={() => {
              nouvelleSeance();
              navigate('/seances/nouvelle');
            }}
            disabled={enregistrement}
          >
            Nouvelle séance
          </Button>
          {seanceId && socle && !lectureSeule && (
            <Button startIcon={<RetirerIcon />} onClick={retirer} disabled={enregistrement}>
              Retirer du socle
            </Button>
          )}
          {seanceId && !lectureSeule && (
            <Button color="error" startIcon={<DeleteIcon />} onClick={supprimer} disabled={enregistrement}>
              Supprimer
            </Button>
          )}
          {estModele && seanceId && USAGES_PAR_DEFAUT.map((u: InfoUsageParDefaut) =>
            usagesDefaut.includes(u.code) ? (
              <Chip key={u.code} color="secondary" icon={<StarIcon />} label={`Modèle ${u.libelle.toLowerCase()}`} sx={{ alignSelf: 'center' }} />
            ) : (
              <Button key={u.code} startIcon={<StarIcon />} onClick={() => choisirCommeModeleParDefaut(u.code)} disabled={enregistrement}>
                Modèle {u.libelle.toLowerCase()}
              </Button>
            ),
          )}
        </Box>
      </Paper>

      {synthese?.erreur && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {synthese.erreur}
        </Alert>
      )}

      {synthese && !synthese.erreur && (
        <Box sx={{ mt: 3 }}>
          <Typography sx={{ mb: 2 }}>
            <b>Durée totale :</b> {Math.floor(synthese.dureeTotaleS / 60)} min ({synthese.dureeTotaleS.toFixed(0)} s)
            {' — '}
            <b>Distance totale :</b> {formatDistanceKm(synthese.distanceTotaleM)}
            {' — '}
            <b>UA (charge) :</b> {Math.round(synthese.chargeUA)}
            {' — '}
            <b>RPE séance :</b> {synthese.rpeSeance}/10
          </Typography>
          {/* Visualisation : avec l'echauffement et la recuperation du modele applique au calendrier */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Visualisation{modeleApplique ? ` (avec le modèle « ${modeleApplique.nom} »)` : ''}
            </Typography>
            <ProfilSeance
              notations={{
                echauffement: modeleApplique ? partiesDefaut.echauffement?.notation : null,
                corps: notation,
                recuperation: modeleApplique ? partiesDefaut.recuperation?.notation : null,
              }}
              vma={vma}
              table={table}
            />
          </Paper>
          {(typeManuel || typeAuto) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <b>Type :</b>
              <TypeSeanceChip code={typeManuel || typeAuto} force={!!typeManuel} />
              {typeManuel && typeAuto && typeManuel !== typeAuto && (
                <Typography variant="caption" color="text.secondary">
                  (automatique : {typeSeance(typeAuto)?.libelle})
                </Typography>
              )}
            </Box>
          )}

          {detail
            .filter((row) => row.erreur)
            .map((row, index) => (
              <Alert severity="error" sx={{ mb: 1 }} key={index}>
                {row.erreur}
              </Alert>
            ))}

          <DataGrid
            rows={detail.filter((row) => !row.erreur).map((row, index) => ({ id: index, ...row }))}
            columns={columns}
            getRowHeight={() => 'auto'}
            hideFooterPagination
            initialState={{
              pagination: { paginationModel: { pageSize: 100 } },
            }}
            sx={{ backgroundColor: 'background.paper' }}
            // Le tri n'a pas de sens ici : les lignes suivent deja l'ordre
            // de la notation saisie (l'ordre des blocs/repetitions de la
            // seance), qu'il ne faut pas melanger.
            disableColumnSorting
          />

          <ApercuIntervalsIcu
            notation={notationAjustee}
            vma={vma}
            table={table}
            {...(modeleApplique
              ? {
                  nomModele: modeleApplique.nom,
                  echauffement: partiesDefaut.echauffement ? ajusterNotation(partiesDefaut.echauffement.notation, ajustementRecup) : undefined,
                  retourCalme: partiesDefaut.recuperation ? ajusterNotation(partiesDefaut.recuperation.notation, ajustementRecup) : undefined,
                }
              : {})}
          />
        </Box>
      )}

      {message && (
        <Snackbar open autoHideDuration={6000} onClose={() => setMessage(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert severity={message.severity} onClose={() => setMessage(null)}>
            {message.texte}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
}
